import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { DefaultLanguage } from 'app/i18/i18'
import { RoutingPaths } from 'app/routes/routes'

import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket'
import ErrorImage from 'shared/images/error.png'
import { copyText } from 'shared/lib/copyText'
import { getErrorMessage } from 'shared/lib/errorText'
import { openExternalUrl } from 'shared/lib/external-open'
import { getLocalStore } from 'shared/lib/local-store'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { message } from 'shared/ui/message'
import { PopupContent } from 'shared/ui/poup-content'

import { buildEsimUrl } from './lib/build-esim-url'
import s from './styles.module.scss'

const EsimSource =
  import.meta.env.VITE_PROJECT_IS_DEV === 'true'
    ? 'https://stage-esim.mtravel.kg'
    : 'https://esim.mtravel.kg'

const supportedLangs = ['ru', 'en', 'ky']

const EsimActions = {
  close: {
    key: 'close',
    action: 'esim-close',
  },
  copy: {
    key: 'copy',
    action: 'esim-copy',
  },
  pay: {
    key: 'pay',
    action: 'esim-pay',
  },
  install: {
    key: 'install',
    action: 'esim-install',
  },
}

interface EsimMessage {
  type: string
  source: string
  theme: string
  text?: string
  order_id?: string
  lpa?: string
  url?: string
  plan_id?: string
  amount?: number
}

const EsimPage = observer(() => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const isPaying = useRef(false)

  const locationState = useLocation()?.state as { lang: string } | null

  const storedLang = userStore.getClientStateString('language')

  const lang: string = locationState?.lang ?? storedLang ?? DefaultLanguage

  const langParam = supportedLangs.includes(lang) ? lang : DefaultLanguage

  const token = getLocalStore('mTravelToken') || ''

  // `/esim/my-sims` -> `my-sims`: the rest of the path opens the same
  // screen inside the eSIM service.
  const { '*': esimPath } = useParams()

  const linkOpenEsim = buildEsimUrl({
    source: EsimSource,
    path: esimPath,
    token,
    lang: langParam,
  })

  const handleCopy = async (text: string) => {
    await copyText(text)

    message.copy(t('copied_to_clipboard'))
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const handlePay = async (orderId: string, price: string) => {
    if (isPaying.current || !price) return

    isPaying.current = true

    try {
      const { data } = await BuyTicketApi.buyTicketConfirm({
        price,
        id: orderId,
        type: 'esim',
      })

      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      isPaying.current = false
    }
  }

  const handleClose = () => {
    const historyIdx = (window.history.state as { idx?: number } | null)?.idx

    if (historyIdx) {
      navigate(-1)
    } else {
      navigate(RoutingPaths.path())
    }
  }

  const handleMessage = (event: MessageEvent) => {
    if (event?.origin !== EsimSource) return

    const data: EsimMessage = event?.data

    console.log('[event data]', data)

    switch (data?.type) {
      case EsimActions.close.action:
        handleClose()
        break

      case EsimActions.copy.action:
        if (data?.text) handleCopy(data.text)
        break

      case EsimActions.pay.action:
        console.log('[data]', data)

        if (data?.order_id) handlePay(data.order_id, String(data?.amount || 0))
        break
      case EsimActions.install.action:
        if (data?.url) openExternalUrl(data.url)
        break

      default:
        break
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  return (
    <div className={s.container}>
      <iframe
        title="Esim"
        className={s.frame}
        allow="fullscreen"
        src={linkOpenEsim}
      />
    </div>
  )
})

export default EsimPage
